import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';

import { fitnessAppointmentSettings } from '../../api/fitnessAppointmentService';

import '../../styles/pages/fitnessAppointment.scss';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('tr', tr);

const FITNESS_COORDS = Object.freeze({
  lat: 36.583349726154836,
  lng: 36.18281913855207,
});

const initialForm = {
  firstName: '',
  lastName: '',
  identityNumber: '',
  email: '',
  phone: '',
  userGender: '',
  appointmentDate: null,
  slotStartTime: '',
  slotEndTime: '',
};

const FitnessAppointment = () => {
  const [form, setForm] = useState(initialForm);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Form, Step 2: Verification, Step 3: Success
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [appointment, setAppointment] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationResult, setVerificationResult] = useState('');

  const [availability, setAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const maxSelectableDate = useMemo(() => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 2); // Today, tomorrow, day after tomorrow
    maxDate.setHours(0, 0, 0, 0);
    return maxDate;
  }, []);

  useEffect(() => {
    if (!form.appointmentDate) {
      setForm((prev) => ({ ...prev, appointmentDate: minSelectableDate }));
    }
  }, [form.appointmentDate, minSelectableDate]);

  useEffect(() => {
    const fetchSettings = async () => {
      const response = await fitnessAppointmentSettings.getSettings();
      if (response?.isSuccess) {
        setSettings(response.data);
      }
    };
    fetchSettings();
  }, []);

  const formatDateForRequest = (date) => {
    if (!date) return '';
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (!form.appointmentDate) return;

    let cancelled = false;

    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      try {
        const response = await fitnessAppointmentSettings.getAvailability(
          formatDateForRequest(form.appointmentDate)
        );
        if (!cancelled && response?.isSuccess) {
          setAvailability(response.data || []);
          // Automatically select first available if current slot is invalid or empty
          const currentSlotVal = form.slotStartTime;
          const slots = response.data || [];

          if (!currentSlotVal && slots.length > 0) {
            const firstAvailable = slots.find(s => !s.isFull && !s.isBlocked);
            if (firstAvailable) {
              setForm(prev => ({
                ...prev,
                slotStartTime: firstAvailable.slotStartTime,
                slotEndTime: firstAvailable.slotEndTime
              }));
            }
          }
        }
      } catch (err) {
        console.error('Availability fetch error:', err);
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    };

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [form.appointmentDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' || name === 'identityNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      const maxLength = name === 'phone' ? 10 : 11;
      setForm((prev) => ({ ...prev, [name]: digitsOnly.slice(0, maxLength) }));
      return;
    }

    if (name === 'slotStartTime') {
      const selectedSlot = availability.find(s => s.slotStartTime === value);
      if (selectedSlot) {
        setForm(prev => ({
          ...prev,
          slotStartTime: value,
          slotEndTime: selectedSlot.slotEndTime
        }));
        return;
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, appointmentDate: date }));
  };

  const addressLink = useMemo(() => {
    const { lat, lng } = FITNESS_COORDS;
    const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const coordinateStr = `${lat},${lng}`;
    const query = encodeURIComponent('İskenderun Belediyesi Spor Kompleksi, İskenderun/Hatay');

    if (isIOS) {
      return `http://maps.apple.com/?daddr=${coordinateStr}&q=${query}`;
    }

    return `https://www.google.com/maps/dir/?api=1&destination=${coordinateStr}&destination_place_id=&travelmode=driving`;
  }, []);

  const validateForm = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Lütfen ad ve soyad bilgilerinizi giriniz.');
      return false;
    }

    if (!/^\d{11}$/.test(form.identityNumber.trim())) {
      setError('T.C. kimlik numarasi 11 haneli olmalidir.');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Lütfen geçerli bir e-posta adresi giriniz.');
      return false;
    }

    if (form.phone.trim().length !== 10) {
      setError('Telefon numarasi 10 haneli olmalidir.');
      return false;
    }

    if (!form.appointmentDate) {
      setError('Lütfen bir randevu tarihi seçiniz.');
      return false;
    }

    if (!form.slotStartTime) {
      setError('Lütfen bir randevu saati seçiniz.');
      return false;
    }

    const selectedSlotInfo = availability.find(s => s.slotStartTime === form.slotStartTime);
    if (!selectedSlotInfo) {
      setError('Seçtiğiniz saat dilimi geçerli değil.');
      return false;
    }

    if (selectedSlotInfo.isFull) {
      setError('Seçtiğiniz saat için kontenjan dolmuştur.');
      return false;
    }

    if (selectedSlotInfo.isBlocked) {
      setError('Seçtiğiniz saat kapalıdır.');
      return false;
    }

    // Gender validation against settings.genderScheduleJson
    if (settings?.genderScheduleJson && form.userGender) {
      try {
        const schedules = JSON.parse(settings.genderScheduleJson);
        const restriction = schedules.find(s => s.startTime === form.slotStartTime);
        if (restriction && restriction.allowedGender && restriction.allowedGender !== form.userGender) {
          setError(`Seçilen saat dilimi yalnızca ${restriction.allowedGender === 'F' ? 'kadın' : 'erkek'} üyelerimiz için ayrılmıştır.`);
          return false;
        }
      } catch (e) {
        console.error('Invalid gender schedule json', e);
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setVerificationResult('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fitnessAppointmentSettings.requestAppointment({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        identityNumber: form.identityNumber.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        userGender: form.userGender || null,
        appointmentDate: formatDateForRequest(form.appointmentDate),
        slotStartTime: form.slotStartTime,
        slotEndTime: form.slotEndTime
      });

      if (response?.isSuccess) {
        setAppointment(response.data);
        setMessage(
          response.resultMessage ||
          'Randevu talebiniz alındı. Lütfen doğrulama kodunu giriniz.'
        );
        setStep(2);
      } else {
        setError(
          response?.resultMessage ||
          'Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.'
        );
      }
    } catch (err) {
      setError('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();

    if (!appointment?.appointmentId) {
      setVerificationResult('Randevu bilgisi bulunamadı. Lütfen yeniden deneyin.');
      return;
    }

    if (!verificationCode.trim()) {
      setVerificationResult('Lütfen doğrulama kodunu giriniz.');
      return;
    }

    setLoading(true);
    setVerificationResult('');
    setError('');

    try {
      const response = await fitnessAppointmentSettings.confirmAppointment({
        appointmentId: appointment.appointmentId,
        verificationCode: verificationCode.trim(),
      });

      if (response?.isSuccess) {
        setVerificationResult(response.resultMessage || 'Randevunuz onaylanmistir.');
        setStep(3);
      } else {
        setVerificationResult(
          response?.resultMessage || 'Doğrulama tamamlanamadı. Lütfen kodunuzu kontrol edin.'
        );
      }
    } catch (err) {
      setVerificationResult('Dogrulama sirasinda bir hata olustu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fitness-appointment-page">
      <div className="container">
        <header className="fa-header">
          <h1>Fitness Salonu Randevu Sistemi</h1>
          <p>
            Vatandaşlarımızın sağlıklı ve zinde kalmalarını desteklemek amacıyla kurulan Fitness salonumuz için ranevunuzu oluşturabilirsiniz.
          </p>
        </header>

        {error && <div className="fa-alert fa-alert-error">{error}</div>}
        {message && step === 2 && <div className="fa-alert fa-alert-info">{message}</div>}

        {step === 1 && (
          <form className="fa-form" onSubmit={handleSubmit}>
            <section className="fa-info fa-info-inline">
              <p>
                Fitness salonumuz ücretsiz olarak hizmet vermekte olup, kontenjan sınırımız bulunmaktadır. Lütfen gelmeyeceğiniz randevuları iptal ediniz.
              </p>
            </section>

            <div className="fa-grid">
              <div className="fa-field fa-field-firstName">
                <label htmlFor="firstName">Adınız</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Adınız"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  maxLength={100}
                />
              </div>

              <div className="fa-field fa-field-lastName">
                <label htmlFor="lastName">Soyadınız</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Soyadınız"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  maxLength={100}
                />
              </div>

              <div className="fa-field fa-field-identity">
                <label htmlFor="identityNumber">T.C. Kimlik No</label>
                <input
                  id="identityNumber"
                  name="identityNumber"
                  type="text"
                  inputMode="numeric"
                  placeholder="T.C. Kimlik No"
                  value={form.identityNumber}
                  onChange={handleChange}
                  required
                  minLength={11}
                  maxLength={11}
                />
              </div>

              <div className="fa-field fa-field-contact">
                <div className="contact-field">
                  <label htmlFor="email">E-posta Adresi</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="E-posta"
                    value={form.email}
                    onChange={handleChange}
                    required
                    maxLength={150}
                  />
                </div>
                <div className="contact-field">
                  <label htmlFor="phone">Cep Telefonu</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="5XX XXX XX XX"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    maxLength={10}
                  />
                </div>
                <div className="contact-field">
                  <label htmlFor="userGender">Cinsiyet</label>
                  <select
                    id="userGender"
                    name="userGender"
                    value={form.userGender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seçiniz</option>
                    <option value="M">Erkek</option>
                    <option value="F">Kadın</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="fa-datepicker-section" style={{ marginBottom: '20px' }}>
              <label className="fa-datepicker-label">Tarih Seçimi</label>
              <div className="fa-date-buttons">
                {Array.from({ length: 3 }).map((_, i) => {
                  const d = new Date();
                  d.setDate(d.getDate() + i);
                  d.setHours(0, 0, 0, 0);

                  const isSelected = form.appointmentDate?.getTime() === d.getTime();
                  const labels = ['Bugün', 'Yarın', 'Sonraki Gün'];

                  return (
                    <button
                      key={i}
                      type="button"
                      className={`fa-date-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => handleDateChange(d)}
                    >
                      <span className="fa-date-label">{labels[i]}</span>
                      <span className="fa-date-value">
                        {d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="fa-field" style={{ marginBottom: '20px' }}>
              <label>1. Saat Seçimi</label>
              {availabilityLoading ? (
                <div style={{ padding: '10px' }}>Slotlar yükleniyor...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', width: '100%' }}>
                  {availability.length === 0 ? (
                    <div style={{ padding: '10px', color: '#6b7280' }}>Hiçbir seans bulunamadı.</div>
                  ) : (
                    availability.map(slot => {
                      const isDisabled = slot.isFull || slot.isBlocked;
                      const isSelected = form.slotStartTime === slot.slotStartTime;

                      // Check gender restriction based on settings (Optional frontend check, helps UX)
                      let isGenderRestricted = false;
                      let allowedGenderStr = '';
                      if (settings?.genderScheduleJson) {
                        try {
                          const schedules = JSON.parse(settings.genderScheduleJson);
                          const restriction = schedules.find(s => s.startTime === slot.slotStartTime);
                          if (restriction && restriction.allowedGender) {
                            if (form.userGender && restriction.allowedGender !== form.userGender) {
                              isGenderRestricted = true;
                            }
                            allowedGenderStr = restriction.allowedGender === 'F' ? 'KADIN' : 'ERKEK';
                          }
                        } catch (e) { }
                      }

                      return (
                        <button
                          key={slot.slotStartTime}
                          type="button"
                          disabled={isDisabled || isGenderRestricted}
                          onClick={() => handleChange({ target: { name: 'slotStartTime', value: slot.slotStartTime } })}
                          style={{
                            padding: '12px 10px',
                            border: `2px solid ${isSelected ? '#0f75bc' : '#d1d5db'}`,
                            borderRadius: '8px',
                            background: isSelected ? '#eff6ff' : (isDisabled || isGenderRestricted ? '#f3f4f6' : '#fff'),
                            cursor: (isDisabled || isGenderRestricted) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            opacity: (isDisabled || isGenderRestricted) ? 0.6 : 1,
                            transition: 'all 0.2s'
                          }}
                        >
                          <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: isSelected ? '#0f75bc' : '#374151' }}>
                            {slot.slotStartTime} - {slot.slotEndTime}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: slot.isFull ? '#dc2626' : '#059669', marginTop: '4px' }}>
                            {slot.isBlocked ? 'Kapalı' : (slot.isFull ? 'Dolu' : `${settings?.maxCapacityPerSlot - slot.reservedCount} Kişi Boş`)}
                          </span>
                          {allowedGenderStr && (
                            <span style={{ fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: allowedGenderStr === 'KADIN' ? '#fdf2f8' : '#eff6ff', color: allowedGenderStr === 'KADIN' ? '#be185d' : '#1d4ed8', padding: '2px 6px', borderRadius: '4px', marginTop: '4px' }}>
                              {allowedGenderStr}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className="fa-submit" style={{ width: '100%', marginTop: '20px' }}>
              {loading ? 'İşleniyor...' : 'Randevu Oluştur'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="fa-form fa-form-verify" onSubmit={handleVerification}>
            <div className="fa-summary">
              <h3>Dogrulama Kodu Gerekli</h3>
              <p>
                Lütfen e-posta adresinize gönderilen doğrulama
                kodunu aşağıdaki alana giriniz.
              </p>
            </div>

            {verificationResult && (
              <div className="fa-alert fa-alert-error" style={{ textAlign: 'center', margin: '0 auto 20px' }}>
                {verificationResult}
              </div>
            )}

            <div className="fa-field" style={{ alignItems: 'center' }}>
              <label htmlFor="otp" style={{ textAlign: 'center' }}>Doğrulama Kodu</label>
              <input
                id="otp"
                name="verificationCode"
                type="text"
                placeholder=""
                style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: '1.2rem' }}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="off"
                required
                maxLength={6}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <button type="submit" disabled={loading} className="fa-submit" style={{ minWidth: '200px' }}>
                {loading ? 'Dogrulaniyor...' : 'Doğrula'}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="fa-success">
            <h3>Randevunuz Onaylandı!</h3>
            <p>
              Talebiniz alınmış olup onaylanmıştır. Belirttiğiniz saat aralığında kompleksimize giriş yapabilirsiniz.
            </p>

            {appointment && (
              <div className="fa-code-card">
                <span className="fa-code-label">Tarih</span>
                <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {new Date(appointment.appointmentDate).toLocaleDateString('tr-TR')}
                </span>
                <span className="fa-code-label" style={{ marginTop: '10px' }}>Saat</span>
                <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {appointment.slotStartTime} - {appointment.slotEndTime}
                </span>
              </div>
            )}

            <p className="fa-success-message" style={{ marginTop: '20px' }}>İlginiz için teşekkür ederiz.</p>
          </div>
        )}

        {addressLink && (
          <div className="fa-location-only">
            <a href={addressLink} target="_blank" rel="noopener noreferrer" className="fa-location-link" title="Haritada Aç">
              <span className="fa-location-label">Fitness Kompleksimiz</span>
              <span className="fa-location-address">
                <span className="fa-location-icon" />
                Sakarya, Şht. Astsb. Kd. Çvş. Ali Ermercan Cd., 31240 İskenderun/Hatay
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default FitnessAppointment;
